import { useState } from 'react';

import { CostCalculationExternalSurchargeSetting } from 'generated';
import { useTranslation } from 'react-i18next';

import { formatByLocale, fromISOString } from 'utils/date';
import { displayCurrency, displayPercentage } from 'utils/misc';

import { queries } from 'RestAPIs/rapini';

import {
  GridColumns,
  GridRenderCellParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';

import { DataGrid } from 'components/shared';
import { AddEditCostCalculationExternalSetting } from 'components/shared/CostCalculationExternalSurcharge/AddEditCostCalculationExternalSetting';
import { RecalculateCostCalculationExternal } from 'components/shared/CostCalculationExternalSurcharge/RecalculateCostCalculationExternal';

export type CostCalculationExternalsSettingsGridProperties = {
  employmentId?: number;
  organizationalUnitId?: number;
};

const dateFormatter = (
  params: GridRenderCellParams<any, GridValidRowModel, any>
) => (params.value && formatByLocale(fromISOString(params.value), 'P')) || '-';

export function CostCalculationExternalsSettingsGrid(
  props: CostCalculationExternalsSettingsGridProperties
) {
  const { employmentId, organizationalUnitId } = props;
  const { t } = useTranslation();

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      renderCell: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      renderCell: dateFormatter
    }
  ];
  if (organizationalUnitId) {
    columns.push(
      {
        field: 'function',
        headerName: t('function'),
        renderCell: ({ value }) => (value && value.description) || '-',
        flex: 1,
        minWidth: 200
      },
      {
        field: 'calculateFunctionType',
        headerName: t('calculateFunctionType'),
        renderCell: ({ value }) =>
          t(`calculateFunctionTypeValue.${value}`) || '-',
        flex: 1,
        minWidth: 150
      }
    );
  }
  columns.push(
    {
      field: 'amount',
      headerName: t('baseAmount'),
      renderCell: ({ value }) => (value && displayCurrency(value)) || '-'
    },
    {
      field: 'fee',
      headerName: t('fee'),
      renderCell: ({ value }) => (value && displayPercentage(value)) || '-'
    },
    {
      field: 'vat',
      headerName: t('vat'),
      renderCell: ({ value }) =>
        (value && value.percentage && displayPercentage(value.percentage)) ||
        '-',
      flex: 1
    }
  );

  const { data: settings } =
    queries.useGetCostCalculationExternalSurchargeSettings(
      employmentId,
      organizationalUnitId
    );
  const [
    shouldShowCostCalculationExternalSetting,
    setShouldShowCostCalculationExternalSetting
  ] = useState<boolean>(false);
  const [
    costCalculationExternalSettingData,
    setCostCalculationExternalSettingData
  ] = useState<CostCalculationExternalSurchargeSetting | null>(null);

  function handleAddCostCalculationExternalSettingClick() {
    setCostCalculationExternalSettingData(null);
    setShouldShowCostCalculationExternalSetting(true);
  }
  function handleEditCostCalculationExternalSettingClick(id: number) {
    setCostCalculationExternalSettingData(
      settings?.find(
        (setting) => setting.id == id
      ) as CostCalculationExternalSurchargeSetting
    );
    setShouldShowCostCalculationExternalSetting(true);
  }
  function handleClose() {
    setShouldShowCostCalculationExternalSetting(false);
  }

  const [
    shouldShowRecalculateCostCalculationExternal,
    setShouldShowRecalculateCostCalculationExternal
  ] = useState<boolean>(false);

  function handleCloseRecalculateCostCalculationExternal() {
    setShouldShowRecalculateCostCalculationExternal(false);
  }

  const additionalButtons = [
    {
      label: t('recalculate'),
      onClick: () => {
        setShouldShowRecalculateCostCalculationExternal(true);
      }
    }
  ];

  return (
    <>
      <DataGrid
        title={t('costCalculationExternals')}
        columns={columns}
        rows={settings || []}
        onEditClick={(id) => handleEditCostCalculationExternalSettingClick(+id)}
        onAddClick={() => handleAddCostCalculationExternalSettingClick()}
        additionalButtons={additionalButtons}
      />
      <AddEditCostCalculationExternalSetting
        isOpen={shouldShowCostCalculationExternalSetting}
        employmentId={employmentId}
        organizationalUnitId={organizationalUnitId}
        handleClose={handleClose}
        setting={costCalculationExternalSettingData}
      />
      <RecalculateCostCalculationExternal
        employmentId={employmentId}
        organizationalUnitId={organizationalUnitId}
        isOpen={shouldShowRecalculateCostCalculationExternal}
        handleClose={handleCloseRecalculateCostCalculationExternal}
      />
    </>
  );
}
